import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { XIcon, ExternalLinkIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import DashboardDetail from '../../pages/analysis/DashboardDetail';
import { Button } from '../ui/button';

interface DashboardViewerOverlayProps {
  dashboardId: string;
  name?: string;
  onClose: () => void;
}

/**
 * Full-screen takeover showing a dashboard without leaving the conversation.
 *
 * A plain portalled overlay rather than a Dialog: the dashboard grid measures
 * its own width to lay panels out, and Radix's focus trap + animated mount
 * resizes it mid-measure, which lands panels at the wrong widths on open.
 *
 * DashboardDetail is rendered chromeless — the app shell is already behind the
 * overlay, and a second sidebar inside it would be absurd.
 */
export const DashboardViewerOverlay: React.FC<DashboardViewerOverlayProps> = ({
  dashboardId,
  name,
  onClose,
}) => {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    // The page behind must not scroll while the takeover is up.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-50 bg-background flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-label={name ? `Dashboard: ${name}` : 'Dashboard'}
    >
      <div className="flex items-center justify-between gap-3 px-4 py-2 border-b border-border flex-shrink-0">
        <div className="min-w-0">
          <p className="text-sm font-medium truncate m-0">{name || 'Dashboard'}</p>
          <p className="text-xs text-muted-foreground m-0">Press Esc to go back to the conversation</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button variant="outline" size="sm" asChild>
            <Link to={`/analytics/dashboards/${dashboardId}`} target="_blank" rel="noopener noreferrer">
              <ExternalLinkIcon className="h-4 w-4 mr-1" />
              Open full page
            </Link>
          </Button>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close dashboard">
            <XIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-hidden">
        <DashboardDetail embeddedDashboardId={dashboardId} chromeless fillParent />
      </div>
    </div>,
    document.body,
  );
};
